/**
 * Reusable Input component with error states
 * Patient Survey SPA
 */

import React, { useId } from 'react';
import styles from './Input.module.css';

/**
 * Input type variants
 */
export type InputType = 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'password' | 'date';

/**
 * Input size types
 */
export type InputSize = 'sm' | 'md' | 'lg';

/**
 * Base props shared between input and textarea
 */
interface BaseInputProps {
  /** Label text for the input */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Size variant of the input */
  size?: InputSize;
  /** Additional hint text below the input */
  hint?: string;
  /** Whether the input should take full width */
  fullWidth?: boolean;
}

/**
 * Props for standard input element
 */
export interface InputProps
  extends BaseInputProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Type of input - use 'textarea' for multiline */
  type?: Exclude<InputType, 'textarea'>;
}

/**
 * Props for textarea element
 */
export interface TextareaProps
  extends BaseInputProps,
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Type must be 'textarea' for multiline input */
  type: 'textarea';
  /** Number of visible text rows */
  rows?: number;
}

/**
 * Combined props type for the Input component
 */
export type InputComponentProps = InputProps | TextareaProps;

/**
 * Type guard to check if props are for textarea
 */
function isTextareaProps(props: InputComponentProps): props is TextareaProps {
  return props.type === 'textarea';
}

/**
 * Reusable Input component with support for various types, sizes, and error states
 *
 * @example
 * // Basic text input
 * <Input label="Name" value={name} onChange={handleChange} />
 *
 * @example
 * // Input with error
 * <Input label="Email" type="email" error="Please enter a valid email" />
 *
 * @example
 * // Required input
 * <Input label="Required Field" required />
 *
 * @example
 * // Textarea
 * <Input type="textarea" label="Comments" rows={4} />
 */
export const Input: React.FC<InputComponentProps> = (props) => {
  const {
    label,
    error,
    required = false,
    size = 'md',
    hint,
    fullWidth = false,
    className,
    disabled = false,
    id: providedId,
  } = props;

  // Generate unique IDs for accessibility
  const generatedId = useId();
  const inputId = providedId || generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  // Build aria-describedby based on present elements
  const describedByParts: string[] = [];
  if (error) describedByParts.push(errorId);
  if (hint && !error) describedByParts.push(hintId);
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

  // Container classes
  const containerClasses = [
    styles.inputContainer,
    fullWidth ? styles.fullWidth : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  // Input/Textarea classes
  const inputClasses = [
    styles.input,
    styles[size],
    error ? styles.hasError : '',
    disabled ? styles.disabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Shared props for both input and textarea
  const sharedProps = {
    id: inputId,
    className: inputClasses,
    disabled,
    required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': ariaDescribedBy,
    'aria-required': required,
  };

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {isTextareaProps(props) ? (
        <textarea
          {...sharedProps}
          rows={props.rows || 3}
          placeholder={props.placeholder}
          value={props.value}
          onChange={props.onChange}
          maxLength={props.maxLength}
        />
      ) : (() => {
        const { type, label: _l, error: _e, required: _r, size: _s, hint: _h, fullWidth: _fw, className: _c, disabled: _d, id: _id, ...inputHtmlProps } = props as InputProps;
        return (
          <input
            {...sharedProps}
            {...inputHtmlProps}
            type={type || 'text'}
          />
        );
      })()}

      {error && (
        <span id={errorId} className={styles.errorMessage} role="alert">
          <svg
            className={styles.errorIcon}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </span>
      )}

      {hint && !error && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
};

export default Input;
